import { useCallback, useState } from 'react';

/**
 * First-run onboarding state for the Evidence module.
 *
 * The wizard walks an admin through the three things that turn an empty
 * workspace into a working evidence practice: **enable** the module, **ingest**
 * a first asset, and see how to **verify** it. Two of those three are real
 * side-effecting handoffs — enabling calls the org toggle, ingesting opens the
 * real browser-capture flow — so the wizard mostly *routes*; it does not
 * reimplement anything.
 *
 * What lives here is only the bookkeeping the live data cannot answer on its
 * own:
 *
 * - **dismissed** — the operator chose "skip for now". Persisted, so the wizard
 *   does not reappear on every visit; a quiet "Setup guide" link reopens it.
 * - **verifyAcked** — verification is a thing you learn, not a row that
 *   appears, so the last step is acknowledged rather than detected.
 *
 * Everything else — is it enabled, is there an asset yet — is read from the
 * live evidence queries and passed in, so the wizard can never disagree with
 * the screen under it. That is why `currentStep` is a pure function.
 */

export type OnboardingStep = 'enable' | 'ingest' | 'verify' | 'done';

const DISMISS_KEY = (orgId: string) => `evac.evidence.onboarding.dismissed.${orgId}`;
const VERIFY_KEY = (orgId: string) => `evac.evidence.onboarding.verified.${orgId}`;

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // The choice still holds for this session; it just will not be remembered.
  }
}

/** The step the operator is on, derived purely from live progress. */
export function currentStep(input: {
  enabled: boolean;
  hasAsset: boolean;
  verifyAcked: boolean;
}): OnboardingStep {
  if (!input.enabled) return 'enable';
  if (!input.hasAsset) return 'ingest';
  if (!input.verifyAcked) return 'verify';
  return 'done';
}

export interface Onboarding {
  dismissed: boolean;
  dismiss: () => void;
  reopen: () => void;
  verifyAcked: boolean;
  ackVerify: () => void;
}

export function useOnboarding(orgId: string): Onboarding {
  const [dismissed, setDismissed] = useState<boolean>(() => readFlag(DISMISS_KEY(orgId)));
  const [verifyAcked, setVerifyAcked] = useState<boolean>(() => readFlag(VERIFY_KEY(orgId)));

  const dismiss = useCallback(() => {
    writeFlag(DISMISS_KEY(orgId), true);
    setDismissed(true);
  }, [orgId]);

  const reopen = useCallback(() => {
    writeFlag(DISMISS_KEY(orgId), false);
    setDismissed(false);
  }, [orgId]);

  const ackVerify = useCallback(() => {
    writeFlag(VERIFY_KEY(orgId), true);
    setVerifyAcked(true);
  }, [orgId]);

  return { dismissed, dismiss, reopen, verifyAcked, ackVerify };
}
